import { DefaultNumberInputField } from '~/common/components/defaultUIComponents/DefaultNumberInputField';
import { CreateRoutineValuesSchema } from '../../common/validators/createRoutineValidator';
import { InputField } from '~/common/components/defaultUIComponents/InputField';
import { RoutineApi } from '~/common/api/zeiterfassungServerApi/RoutineApi';
import { Heading } from '~/common/components/defaultUIComponents/Heading';
import { Button } from '~/common/components/defaultUIComponents/Button';
import { secondsToHoursMinutesSeconds } from '~/common/utils/timeUtils';
import { useNavigate, useParams } from 'react-router-dom';
import routes from '~/common/constants/routes.json';
import React, { useCallback, useMemo } from 'react';
import { Flex, Stack } from '@chakra-ui/react';
import { Form, Formik } from 'formik';

interface RoutineValues {
  name: string;
  durationHours: number;
  durationMinutes: number;
  durationSeconds: number;
  description: string;
}

const CreateEditRoutine: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();

  const [{ data: routine }] = RoutineApi.hooks.useRoutineQuery(
    { id },
    { enabled: !!id },
  );
  const { mutateAsync: createRoutine } =
    RoutineApi.hooks.useCreateRoutineMutation();
  const { mutateAsync: updateRoutine } =
    RoutineApi.hooks.useUpdateRoutineMutation();

  const initialValues = useMemo(() => {
    const {
      hours: durationHours,
      minutes: durationMinutes,
      seconds: durationSeconds,
    } = secondsToHoursMinutesSeconds(routine?.duration);
    return {
      name: routine?.name ?? '',
      description: routine?.description ?? '',
      durationHours,
      durationMinutes,
      durationSeconds,
    } as RoutineValues;
  }, [routine]);

  const handleSubmit = useCallback(
    async (values: RoutineValues) => {
      if (id) {
        await updateRoutine({
          id,
          name: values.name,
          description: values.description,
          duration:
            values.durationHours * 60 * 60 +
            values.durationMinutes * 60 +
            values.durationSeconds,
          employees: routine?.employees?.map((e) => e.id) ?? [],
        });
        return navigate(routes.ROUTINES);
      } else {
        await createRoutine({
          name: values.name,
          description: values.description,
          duration:
            values.durationHours * 60 * 60 +
            values.durationMinutes * 60 +
            values.durationSeconds,
        });
        return navigate(routes.ROUTINES);
      }
    },
    [routine, history],
  );

  return (
    <>
      <Heading>{!id ? 'createRoutine.heading' : 'editRoutine.heading'}</Heading>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={CreateRoutineValuesSchema}
        validateOnChange
        enableReinitialize
      >
        {({ values, setValues }) => {
          if (values.durationSeconds > 60)
            void setValues({
              ...values,
              durationSeconds: 59,
            });
          else if (values.durationMinutes > 60)
            void setValues({
              ...values,
              durationMinutes: 59,
            });
          else if (
            values.durationSeconds > 59 &&
            values.durationMinutes >= 59
          ) {
            void setValues({
              ...values,
              durationSeconds: 0,
              durationMinutes: 0,
              durationHours: values.durationHours + 1,
            });
          } else if (values.durationMinutes > 59) {
            void setValues({
              ...values,
              durationMinutes: 0,
              durationHours: values.durationHours + 1,
            });
          } else if (values.durationSeconds > 59) {
            void setValues({
              ...values,
              durationSeconds: 0,
              durationMinutes: values.durationMinutes + 1,
            });
          }
          return (
            <Form>
              <Stack>
                <InputField
                  name="name"
                  label="createRoutine.name.label"
                  placeholder=""
                />
                <InputField
                  name="description"
                  label="createRoutine.description.label"
                  placeholder=""
                  textarea
                  mb={5}
                />
                <Flex>
                  <DefaultNumberInputField
                    name="durationHours"
                    label="createRoutine.durationInHours.label"
                    min={0}
                  />
                  <DefaultNumberInputField
                    name="durationMinutes"
                    label="createRoutine.durationInMinutes.label"
                    min={0}
                    maxInputLenght={2}
                  />
                </Flex>
                <Button type="submit" w="100%">
                  {!id
                    ? 'createRoutine.submit.text'
                    : 'editRoutine.submit.text'}
                </Button>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default CreateEditRoutine;
